'use client'

import { useSignals } from '@preact/signals-react/runtime'
import type { Locale } from '@susu/headless-commerce/config/locale'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import type { Product } from '@susu/headless-commerce/gql/generated/graphql'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import { trackClickProduct } from '@susu/headless-commerce/utils/tracking/tracking'
import Image from 'next/image'
import type { ReactNode } from 'react'
import { useCallback } from 'react'

import { useLocale } from '@susu/headless-commerce/contexts/locale/client'
import {
  fallbackListName,
  listNameMap,
} from '../CMSPageSections/ShopCollectionBlockSection/listNameMap'
import styles from './ProductCard.module.scss'
import { CUSTOMIZE_ICON, badgesMap } from './ProductCardConfig'

export type ProductTrackingData = {
  list: string
  listId: string
  position: number
}

export type ClientProductCardImageLinkProps = {
  children?: ReactNode
  product: Product
  tracking: Omit<ProductTrackingData, 'list'>
  url: string
}

export default function ClientProductCardImageLink({
  tracking,
  product,
  children,
  url,
}: Readonly<ClientProductCardImageLinkProps>) {
  useSignals()
  const country = useCountry()
  const locale = useLocale()
  const pageType = usePageType()

  const handleOnClick = useCallback(() => {
    trackClickProduct({
      enrichProps: {
        country,
        locale: locale as Locale,
      },
      product,
      tracking: {
        ...tracking,
        list: listNameMap[pageType] || fallbackListName,
      },
      pageType,
      quantity: 1,
      variant: product.type === 'item' ? 'one-size' : '',
    })
  }, [country, locale, pageType, product, tracking])

  const {
    availabilityStatus,
    badge,
    isCustomMadeProduct,
    isEnabledCustomizeButton,
    fabricCodeID,
    isFabricAvailable,
  } = product

  const showCustomMadeIcon =
    isCustomMadeProduct ??
    ((isEnabledCustomizeButton || isEnabledCustomizeButton === null) &&
      fabricCodeID?.length &&
      isFabricAvailable)

  const productBadge = availabilityStatus?.preOrder
    ? 'Pre-order'
    : ((badge && badgesMap[badge]) ?? '')

  return (
    <a
      className={styles['product-card__image']}
      href={url}
      onClick={handleOnClick}
    >
      <div className={styles['product-card__badges']}>
        {showCustomMadeIcon ? (
          <span
            className={`${styles['product-card__badge-wrap']} ${styles['product-card__badges--customize']}`}
          >
            <Image
              src={CUSTOMIZE_ICON}
              alt={'Look builder icon'}
              width={18}
              height={18}
            />
          </span>
        ) : null}
        {productBadge?.length ? (
          <span className={styles['product-card__badge-label']}>
            {productBadge}
          </span>
        ) : null}
      </div>
      {children}
    </a>
  )
}
